import React from 'react';
import { Music, User, BookOpen, ChevronRight } from 'lucide-react';

const ActivityListItem = ({ activity, onClick }) => {
  // Get icon based on activity type
  const getActivityIcon = () => {
    switch (activity?.type) {
      case 'song_addition':
        return <Music size={20} className="text-purple-400" />;
      case 'profile_update':
        return <User size={20} className="text-blue-400" />;
      case 'course_completion':
        return <BookOpen size={20} className="text-green-400" />;
      default:
        return null;
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'superuser':
        return 'bg-red-500/20 text-red-300';
      case 'editor':
        return 'bg-blue-500/20 text-blue-300';
      case 'talent':
        return 'bg-purple-500/20 text-purple-300';
      default:
        return 'bg-gray-500/20 text-gray-300';
    }
  };

  return (
    <div
      onClick={onClick}
      className="bg-white/5 hover:bg-white/10 rounded-xl p-4 cursor-pointer transition-all duration-200 group"
    >
      <div className="flex items-start gap-4">
        {/* Activity Icon */}
        <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
          {getActivityIcon()}
        </div>

        {/* User Avatar */}
        <div className="flex-shrink-0">
          {activity?.user?.avatar ? (
            <img
              src={activity?.user?.avatar}
              alt={activity?.user?.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-semibold">
              {activity?.user?.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
        </div>

        {/* Activity Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white font-semibold truncate">
                {activity?.user?.name}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(activity?.user?.role)}`}>
                {activity?.user?.role}
              </span>
            </div>
            <span className="text-white/60 text-sm whitespace-nowrap">
              {formatTimestamp(activity?.timestamp)}
            </span>
          </div>

          <p className="text-white/90 text-sm mb-2">
            {activity?.description}
          </p>

          {/* Metadata Tags */}
          {activity?.metadata && (
            <div className="flex items-center gap-2 flex-wrap">
              {activity?.metadata?.musicalTitle && (
                <span className="text-xs px-2 py-1 bg-white/10 text-white/80 rounded">
                  📚 {activity?.metadata?.musicalTitle}
                </span>
              )}
              {activity?.metadata?.lessonSection && (
                <span className="text-xs px-2 py-1 bg-white/10 text-white/80 rounded">
                  📖 {activity?.metadata?.lessonSection}
                </span>
              )}
              {activity?.metadata?.isPerfect && (
                <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded">
                  ⭐ Perfect Score
                </span>
              )}
            </div>
          )}
        </div>

        {/* Navigate Arrow */}
        <div className="flex-shrink-0 text-white/40 group-hover:text-white/80 transition-colors">
          <ChevronRight size={20} />
        </div>
      </div>
    </div>
  );
};

export default ActivityListItem;